// src/services/adminClient.ts
import { httpsCallable, type HttpsCallableResult } from 'firebase/functions';
import { functions } from '../lib/firebase';

type CallableResult<T = any> = Promise<HttpsCallableResult<T>>;

function adminCallable<TReq = any, TRes = any>(name: string) {
  const fn = httpsCallable<TReq, TRes>(functions, name);
  return (payload?: TReq): CallableResult<TRes> => fn((payload ?? {}) as TReq);
}

// Access / invite code controls
export const cfAdminCreateInvite = adminCallable<
  { code?: string; usesRemaining?: number | null; notes?: string },
  { ok: boolean; code?: string }
>('adminCreateInvite');

export const cfAdminListInvites = adminCallable<
  { limit?: number },
  { ok: boolean; invites: any[] }
>('adminListInvites');

export const cfAdminDeleteInvite = adminCallable<
  { code: string },
  { ok: boolean }
>('adminDeleteInvite');

export const cfAdminSetInviteEnabled = adminCallable<
  { code: string; enabled: boolean },
  { ok: boolean }
>('adminSetInviteEnabled');

export const cfAdminSetAccessConfig = adminCallable<
  {
    cap?: number;
    inviteRequired?: boolean;
    requirePromoCode?: boolean;
    waitlistEnabled?: boolean;
    blockNewSignups?: boolean;
  },
  { ok: boolean }
>('adminSetAccessConfig');

export const cfAdminApproveWaitlistedUser = adminCallable<
  { uid?: string; userId?: string },
  { ok: boolean }
>('adminApproveWaitlistedUser');

export const cfAdminSendInviteEmail = adminCallable<
  { to: string; code: string; subject?: string },
  { ok: boolean }
>('adminSendInviteEmail');

// Users / roles / announcements
export const cfAdminDeleteUserAndPosts = adminCallable<
  { uid?: string; userId?: string },
  { ok: boolean; deletedPosts?: number }
>('adminDeleteUserAndPosts');

export const cfAdminUpdateUserRoles = adminCallable<
  { userId: string; roles: { isAdmin?: boolean; isSeller?: boolean } },
  { ok: boolean }
>('adminUpdateUserRoles');

export const cfAdminSendAnnouncement = adminCallable<
  {
    message: string;
    title?: string;
    category?: 'service_notice' | 'maintenance' | 'safety' | 'release_note';
    priority?: 'normal' | 'important' | 'critical';
    notifyUsers?: boolean;
    published?: boolean;
  },
  {
    ok: boolean;
    announcementId?: string;
    notifyUsers?: boolean;
    eligibleCount?: number;
    skippedCount?: number;
  }
>('adminSendAnnouncement');

export const cfAdminBackfillUserHandles = adminCallable<
  { limit?: number },
  { ok: boolean; scanned?: number; updated?: number }
>('adminBackfillUserHandles');

export type AdminUserAuthSummary = {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  disabled: boolean;
  isAnonymous: boolean;
  providerIds: string[];
  creationTime: string | null;
  lastSignInTime: string | null;
};


export type AdminUserDirectoryEntry = {
  uid: string;
  name: string;
  displayName: string;
  username: string;
  handle: string;
  avatar: string;
  photoURL?: string;
  email: string;
  accountType?: string;
  accessStatus?: string;
  waitlistNumber?: number;
  memberNumber?: number;
  isAdmin: boolean;
  isSeller: boolean;
  xp: number;
  level: number;
  createdAt?: number;
};

export const cfAdminListUsers = adminCallable<
  { pageSize?: number; afterUid?: string | null },
  {
    ok: boolean;
    users: AdminUserDirectoryEntry[];
    nextAfterUid: string | null;
    hasMore: boolean;
  }
>('adminListUsers');

export const cfAdminGetUserAuthSummaries = adminCallable<
  { uids: string[] },
  { ok: boolean; users: Record<string, AdminUserAuthSummary> }
>('adminGetUserAuthSummaries');

export type AdminGrantConsumablesResult = {
  ok: boolean;
  requestId: string;
  userId: string;
  actorUid?: string;
  reason: string;
  shellsGranted: number;
  searchLuresGranted: number;
  shellsBefore: number;
  shellsAfter: number;
  searchLuresBefore: number;
  searchLuresAfter: number;
  idempotentReplay?: boolean;
};

export const cfAdminGrantConsumables = adminCallable<
  {
    uid?: string;
    userId?: string;
    shells?: number;
    searchLures?: number;
    reason: string;
    requestId: string;
  },
  AdminGrantConsumablesResult
>('adminGrantConsumables');

export const cfAdminGetUserAdminSnapshot = adminCallable<
  { uid?: string; userId?: string },
  {
    ok: boolean;
    uid: string;
    auth: AdminUserAuthSummary;
    profile: Record<string, any>;
    metrics: { posts: number; catches: number; species: number; waters: number };
    recentCatches: Array<{
      id: string;
      species: string;
      water: string | null;
      timestamp: number | null;
      imageUrl: string | null;
    }>;
    recentPosts: Array<{ id: string; caption: string; timestamp: number | null }>;
  }
>('adminGetUserAdminSnapshot');

// Community moderation
export const cfAdminDeletePost = adminCallable<
  { postId: string },
  { ok: boolean }
>('adminDeletePost');

export const cfAdminEditPost = adminCallable<
  { postId: string; updatedData?: Record<string, unknown>; data?: Record<string, unknown> },
  { ok: boolean }
>('adminEditPost');

// Bazaar / marketplace moderation
export const cfAdminDeleteMarketplaceItem = adminCallable<
  { itemId: number | string },
  { ok: boolean }
>('adminDeleteMarketplaceItem');

export const cfAdminUpdateMarketplaceItem = adminCallable<
  { itemId?: number | string; item?: any; updatedData?: Record<string, unknown> },
  { ok: boolean }
>('adminUpdateMarketplaceItem');

export type BazaarAuditEvent = Record<string, any> & {
  id: string;
  type?: string;
  createdAtMs?: number;
  threadId?: string;
  listingId?: string;
  messageId?: string;
  offerId?: string;
  actorUid?: string;
};

export const cfAdminGetBazaarAuditEvents = adminCallable<
  { query?: string; limit?: number },
  { ok: boolean; events: BazaarAuditEvent[] }
>('adminGetBazaarAuditEvents');

// Tournaments
export const cfAdminDeleteTournament = adminCallable<
  { tournamentId: string },
  { ok: boolean }
>('adminDeleteTournament');

// Reports
export const cfAdminUpdateUserReportStatus = adminCallable<
  { reportId: string; status: 'open' | 'reviewing' | 'resolved' | 'dismissed' | string },
  { ok: boolean }
>('adminUpdateUserReportStatus');

export const cfAdminDeleteUserReport = adminCallable<
  { reportId: string },
  { ok: boolean }
>('adminDeleteUserReport');

export const cfAdminDeleteReportedPost = adminCallable<
  { reportId: string; resolveReport?: boolean },
  { ok: boolean; postId?: string }
>('adminDeleteReportedPost');

export const cfAdminDeleteReportedMarketplaceItem = adminCallable<
  { reportId: string; resolveReport?: boolean },
  { ok: boolean; itemId?: string }
>('adminDeleteReportedMarketplaceItem');

export const cfAdminWarnUserForReport = adminCallable<
  { reportId: string; message: string },
  { ok: boolean }
>('adminWarnUserForReport');

// Support tickets
export const cfAdminUpdateSupportTicket = adminCallable<
  { ticketId: string; status: 'open' | 'reviewing' | 'resolved' | 'closed' | string },
  { ok: boolean }
>('adminUpdateSupportTicket');

// Account deletion queue
export const cfAdminProcessAccountDeletionRequests = adminCallable<
  { limit?: number; dryRun?: boolean },
  { ok: boolean; processed?: number; scanned?: number; completed?: number; failed?: number; errors?: any[] }
>('adminProcessAccountDeletionRequests');

// FishIQ / NOAA pilot aggregate metrics
export const cfAdminGetFishIQPilotMetrics = adminCallable<
  { days?: number; limit?: number },
  {
    ok: boolean;
    windowDays?: number;
    sinceMs?: number;
    generatedAt?: number;
    totals?: {
      observations?: number;
      sharedAggregateEligible?: number;
      privateOnly?: number;
      rollupEligible?: number;
      generalizedPrivacyProtected?: number;
      pilotParticipants?: number;
    };
    topSpecies?: Array<{ label: string; count: number }>;
    topWaters?: Array<{ label: string; count: number }>;
    topRegions?: Array<{ label: string; count: number }>;
    monthBuckets?: Array<{ label: string; count: number }>;
    partnerCodeBuckets?: Array<{ label: string; count: number }>;
    waterClarityBuckets?: Array<{ label: string; count: number }>;
    catchDispositionBuckets?: Array<{ label: string; count: number }>;
  }
>('adminGetFishIQPilotMetrics');



export const cfAdminGetFishIQCalibration = adminCallable<
  { limit?: number },
  {
    ok: boolean;
    scanned: number;
    surfaceable: number;
    hidden: number;
    averageObservationQuality: number;
    strongWithoutDiversity: number;
    tierCounts: Record<string, number>;
    qualityBands: Record<string, number>;
    freshnessBands: Record<string, number>;
    layerCounts: Record<string, number>;
    generatedAtMs: number;
    privacyNote?: string;
  }
>('adminGetFishIQCalibration');

export const cfAdminGrantFoundingMemberRewards = adminCallable<
  { uid?: string; userId?: string },
  { ok: boolean; granted: boolean; memberNumber?: number; reason?: string }
>('adminGrantFoundingMemberRewards');

export const cfAdminBackfillFoundingMemberRewards = adminCallable<
  { limit?: number },
  { ok: boolean; scanned: number; eligible: number; granted: number; skipped: number; failed: number; failures?: any[] }
>('adminBackfillFoundingMemberRewards');
